import React, { useEffect, useState } from 'react';
import { ModuleBase, HeadingTag, Button, Link } from '@/components';
import Plus from '@/assets/plus.svg';
import Minus from '@/assets/minus.svg';
import { isColorDark, themeFromBg } from '@/utils';
import classNames from 'classnames';
import FadeUpAnimation from '@/components/animations/FadeUpAnimation/FadeUpAnimation';
import FadeUpStarter from '@/components/animations/FadeUpAnimation/FadeUpStarter';
import { motion, AnimatePresence } from 'framer-motion';
import { selectShowMenuGlobal } from '@/store/index';
import { useSelector } from 'react-redux';

const AccordionGroupedModule = ({ data }) => {
  const { headingTitle, allowOpeningMultiple, accordionGroups, primaryCTA, backgroundColour } = data || {};

  const [activeIndexList, setActiveIndexList] = useState([]);
  const [activeNavIndex, setActiveNavIndex] = useState(0);

  const showMenu = useSelector(selectShowMenuGlobal);

  const toggle = (groupIndex, index) => {
    const id = `${groupIndex}-${index}`;
    let newArray = [...activeIndexList];
    if (newArray.includes(id)) {
      newArray = newArray.filter((i) => i !== id);
    } else {
      newArray = allowOpeningMultiple ? [id, ...newArray] : [id];
    }
    setActiveIndexList(newArray);
  };

  const scrollToGroup = (id, index) => {
    setActiveNavIndex(index);
    const element = document.getElementById(id);
    if (element) {
      const headerOffset = 124;
      const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
      const offsetPosition = elementPosition - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth',
      });
    }
  };

  useEffect(() => {
    console.log('Redux showMenu state changed:', showMenu);
  }, [showMenu]);

  console.log('Redux showMenu state changed:', showMenu);

  const getPaddingTop = () => {
    if (data?.paddingTop) {
      if (data.paddingTop.toLowerCase() === 'medium') {
        return '!pt-0  lg:!pt-16';
      } else if (data.paddingTop.toLowerCase() === 'large') {
        return '!pt-0 lg:!pt-26';
      } else if (data.paddingTop.toLowerCase() !== 'none') {
        return '!pt-0 lg:!pt-10';
      }
    }
    return '';
  };

  const paddingTop = getPaddingTop();

  return (
    <ModuleBase data={data} className={`relative ${paddingTop}`}>
      <FadeUpStarter className=" lg:container xl:pl-0">
        {headingTitle && (
          <FadeUpAnimation>
            <HeadingTag
              data={headingTitle}
              className="font-ivy-ora text-center text-[56px] font-normal leading-[60px] -tracking-normal color-from-bg lg:text-[80px] lg:leading-[72px] lg:-tracking-ultraWide"
            />
          </FadeUpAnimation>
        )}

        <div className="relative flex flex-col lg:flex-row  gap-8">
          {/* Sticky Nav */}
          <div
            className={`sticky px-4 md:px-6 xl:px-8 transition-all duration-1000 xxl:px-10 overflow-x-auto whitespace-nowrap w-full lg:w-[278px] lg:pl-0 flex items-center z-20 flex-nowrap flex-row lg:flex-col gap-3 bg-secondary-coolsand h-[70px] lg:h-fit lg:top-[124px] shadow-stickyNav lg:shadow-none lg:bg-inherit ${showMenu ? 'top-[76px]' : 'top-0'}`}
          >
            {accordionGroups.map((group, index) => (
              <Link
                key={index}
                link={group.groupLink}
                onClick={() => scrollToGroup(`group-${index}`, index)}
                className={classNames(
                  'text-sm leading-[22px] color-from-bg w-full xl:[&:hover+.image-block]:opacity-100 transition-colors',
                  activeNavIndex === index ? 'text-primary font-medium' : 'opacity-60 font-normal',
                )}
              >
                {group.groupLink?.text}
              </Link>
            ))}
          </div>

          {/* Accordion Content */}
          <div className="flex-1 container">
            {accordionGroups.map((group, groupIndex) => (
              <div key={groupIndex} id={`group-${groupIndex}`} data-scroll-id={`group-${groupIndex}`} className="mb-12">
                <FadeUpAnimation>
                  <h2 className="text-[24px] leading-8 lg:text-[40px] lg:leading-[44px] font-ivy-ora mb-2 color-from-bg">
                    {group.groupName}
                  </h2>
                </FadeUpAnimation>

                {group.accordionElements.map((element, elIndex) => {
                  const id = `${groupIndex}-${elIndex}`;
                  const isOpen = activeIndexList.includes(id);

                  return (
                    <FadeUpAnimation key={id} className="first:pt-0 first:md:pt-4">
                      <button
                        type="button"
                        onClick={() => toggle(groupIndex, elIndex)}
                        className={classNames(
                          'relative pt-6 flex items-start justify-between w-full text-left font-montserrat text-base leading-6.5 lg:pl-[86px] transition-all duration-500',
                          isOpen ? 'opacity-1' : 'opacity-50',
                        )}
                      >
                        <div
                          className={classNames(
                            'flex items-start justify-center transform-none w-[32px] h-[32px] mr-3 absolute lg:left-0  lg:mb-0 mt-[1.5%] lg:mt-[0.5%]',
                          )}
                        >
                          <AnimatePresence mode="wait" initial={false}>
                            {isOpen ? (
                              <motion.div
                                key="minus"
                                initial={{ rotate: -90, opacity: 0 }}
                                animate={{ rotate: 0, opacity: 1 }}
                                exit={{ rotate: 90, opacity: 0 }}
                                transition={{ duration: 0.3 }}
                              >
                                <Minus className="w-[16px] h-[16px] lg:w-[19px] lg:h-[19px] color-from-bg" />
                              </motion.div>
                            ) : (
                              <motion.div
                                key="plus"
                                initial={{ rotate: 90, opacity: 0 }}
                                animate={{ rotate: 0, opacity: 1 }}
                                exit={{ rotate: -90, opacity: 0 }}
                                transition={{ duration: 0.3 }}
                              >
                                <Plus className="w-[16px] h-[16px] lg:w-[19px] lg:h-[19px] color-from-bg" />
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </div>

                        <div
                          className={classNames(
                            'flex justify-between items-center w-full gap-4 transition-all duration-500 pl-[50px] lg:pl-0',
                          )}
                        >
                          <span className="color-from-bg font-ivy-ora text-[24px] font-normal leading-8 -tracking-wider">
                            {element.title}
                          </span>
                          {element.link && (
                            <Button
                              className={classNames(
                                'hidden lg:block transition-opacity duration-500',
                                isOpen ? 'opacity-100' : 'opacity-0',
                              )}
                              theme="dark"
                              link={element.link}
                              type="tertiary"
                            />
                          )}
                        </div>
                      </button>

                      <div className={classNames(isOpen ? 'h-0' : 'h-8', 'helper transition-all duration-400')}></div>

                      <div
                        className={classNames(
                          'h-[1px] transition-all duration-1000 ml-[47px] lg:ml-[86px]',
                          isOpen ? 'opacity-0' : 'opacity-1',
                          isColorDark(backgroundColour) ? 'bg-white/10' : 'bg-black/10',
                        )}
                      />

                      <AnimatePresence initial={false}>
                        {isOpen && (
                          <motion.div
                            key="content"
                            initial="collapsed"
                            animate="open"
                            exit="collapsed"
                            variants={{
                              open: { height: 'auto', opacity: 1 },
                              collapsed: { height: 0, opacity: 0 },
                            }}
                            transition={{ duration: 0.5, ease: [0.4, 0, 0.2, 1] }}
                            className="overflow-hidden ml-[47px] lg:ml-[86px]"
                          >
                            <div
                              className={classNames(isOpen ? 'h-4' : 'h-8', 'helper transition-all duration-400')}
                            ></div>
                            {element.text && (
                              <div
                                dangerouslySetInnerHTML={{ __html: element.text }}
                                className="font-montserrat color-from-bg mb-10 text-sm leading-6 font-normal [&_a]:underline [&_a]:underline-offset-4"
                              />
                            )}
                            {element.link && (
                              <Button
                                className="lg:hidden w-fit mb-8"
                                theme="dark"
                                link={element.link}
                                type="tertiary"
                              />
                            )}
                            <div
                              className={classNames(
                                'h-[1px] transition-all duration-1000',
                                isColorDark(backgroundColour) ? 'bg-white/10' : 'bg-black/10',
                              )}
                            />
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </FadeUpAnimation>
                  );
                })}
              </div>
            ))}

            {primaryCTA && (
              <FadeUpAnimation className="flex justify-center mt-10">
                <div className="group">
                  <Button theme={themeFromBg(isColorDark(backgroundColour))} link={primaryCTA} type={'primary'} />
                </div>
              </FadeUpAnimation>
            )}
          </div>
        </div>
      </FadeUpStarter>
    </ModuleBase>
  );
};

export default AccordionGroupedModule;
